import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    IconButton,
    Tooltip,
    CircularProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Chip,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Translate as TranslateIcon,
    Replay as ResetIcon,
    Language as GlobeIcon,
    Check as CheckIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiService';

const COMMON_LANGUAGES = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Spanish (Español)', flag: '🇪🇸' },
    { code: 'ar', name: 'Arabic (العربية)', flag: '🇸🇦' },
    { code: 'fa', name: 'Persian (فارسی)', flag: '/assets/images/iran_lion_sun.png', isImage: true },
    { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)', flag: '🇮🇳' },
    { code: 'zh-CN', name: 'Simplified Chinese (简体中文)', flag: '🇨🇳' },
    { code: 'zh-TW', name: 'Traditional Chinese (繁體中文)', flag: '🇹🇼' },
];

const ALL_LANGUAGES = [
    ...COMMON_LANGUAGES,
    { code: 'ur', name: 'Urdu (اردو)', flag: '🇵🇰' },
    { code: 'yue', name: 'Cantonese (粵語)', flag: '🇭🇰' },
    { code: 'tl', name: 'Tagalog (Filipino)', flag: '🇵🇭' },
    { code: 'hi', name: 'Hindi (हिन्दी)', flag: '🇮🇳' },
    { code: 'ko', name: 'Korean (한국어)', flag: '🇰🇷' },
    { code: 'ru', name: 'Russian (Русский)', flag: '🇷🇺' },
    { code: 'ja', name: 'Japanese (日本語)', flag: '🇯🇵' },
    { code: 'pt', name: 'Portuguese (Português)', flag: '🇵🇹' },
    { code: 'de', name: 'German (Deutsch)', flag: '🇩🇪' },
    { code: 'tr', name: 'Turkish (Türkçe)', flag: '🇹🇷' },
    { code: 'it', name: 'Italian (Italiano)', flag: '🇮🇹' },
    { code: 'vi', name: 'Vietnamese (Tiếng Việt)', flag: '🇻🇳' },
    { code: 'pl', name: 'Polish (Polski)', flag: '🇵🇱' },
    { code: 'nl', name: 'Dutch (Nederlands)', flag: '🇳🇱' },
    { code: 'th', name: 'Thai (ไทย)', flag: '🇹🇭' },
    { code: 'sv', name: 'Swedish (Svenska)', flag: '🇸🇪' },
    { code: 'id', name: 'Indonesian (Bahasa Indonesia)', flag: '🇮🇩' },
    { code: 'uk', name: 'Ukrainian (Українська)', flag: '🇺🇦' },
    { code: 'he', name: 'Hebrew (עברית)', flag: '🇮🇱' },
    { code: 'ro', name: 'Romanian (Română)', flag: '🇷🇴' },
    { code: 'hu', name: 'Hungarian (Magyar)', flag: '🇭🇺' },
    { code: 'cs', name: 'Czech (Čeština)', flag: '🇨🇿' },
    { code: 'el', name: 'Greek (Ελληνικά)', flag: '🇬🇷' },
].sort((a, b) => a.name.localeCompare(b.name));

const PREFERRED_LANGUAGE_KEY = 'preferred_translation_language';

interface TranslatingTextProps {
    text: string;
    variant?: any;
    color?: string;
    sx?: any;
}

const TranslatingText: React.FC<TranslatingTextProps> = ({ text, variant = 'body1', color, sx }) => {
    const { t, i18n } = useTranslation();
    const [translatedText, setTranslatedText] = useState<string | null>(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [targetLanguage, setTargetLanguage] = useState(ALL_LANGUAGES.find(l => l.code === 'en') || ALL_LANGUAGES[0]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem(PREFERRED_LANGUAGE_KEY);
        if (saved) {
            setTargetLanguage(JSON.parse(saved));
        } else {
            const current = i18n.language.startsWith('zh') ? i18n.language : i18n.language.split('-')[0];
            const found = ALL_LANGUAGES.find(l => l.code === current) || ALL_LANGUAGES.find(l => l.code === 'en') || ALL_LANGUAGES[0];
            setTargetLanguage(found);
        }
    }, [i18n.language]);

    const handleTranslate = async (selectedLang = targetLanguage) => {
        if (isTranslating) return;

        setIsTranslating(true);
        setError(null);
        setAnchorEl(null);

        try {
            const response = await apiService.translateText(text, selectedLang.code);
            if (response.success) {
                setTranslatedText(response.data.translatedText);
            } else {
                setError('Translation failed');
            }
        } catch (err) {
            console.error('Translation error:', err);
            setError('Could not translate text');
        } finally {
            setIsTranslating(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setSearchQuery('');
        setShowMore(false);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const selectLanguage = (lang: typeof ALL_LANGUAGES[0]) => {
        setTargetLanguage(lang);
        localStorage.setItem(PREFERRED_LANGUAGE_KEY, JSON.stringify(lang));
        handleTranslate(lang);
    };

    const handleReset = () => {
        setTranslatedText(null);
        setError(null);
    };

    const displayedLanguages = searchQuery
        ? ALL_LANGUAGES.filter(l =>
            l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            l.code.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : (showMore ? ALL_LANGUAGES : COMMON_LANGUAGES);

    if (!text) return null;

    return (
        <Box sx={{ position: 'relative', width: '100%', ...sx }}>
            <Typography variant={variant} color={color} sx={{ display: 'inline' }}>
                {translatedText || text}
            </Typography>

            {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 0.75, alignItems: 'center' }}>
                {!translatedText ? (
                    <>
                        <Tooltip title={`${t('common.translateTo', 'Translate to')} ${targetLanguage.name}`}>
                            <Chip
                                icon={isTranslating ? <CircularProgress size={14} color="inherit" /> : <TranslateIcon sx={{ fontSize: '14px !important' }} />}
                                label={targetLanguage.name}
                                onClick={() => handleTranslate()}
                                onDelete={handleMenuOpen}
                                deleteIcon={<GlobeIcon sx={{ fontSize: '14px !important' }} />}
                                size="small"
                                variant="outlined"
                                disabled={isTranslating}
                                sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    borderRadius: '12px',
                                    bgcolor: 'rgba(0,0,0,0.03)',
                                    borderColor: 'rgba(0,0,0,0.1)',
                                    '& .MuiChip-label': { px: 1 },
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                                }}
                            />
                        </Tooltip>
                    </>
                ) : (
                    <Tooltip title={t('common.showOriginal', 'Show Original')}>
                        <Chip
                            icon={<ResetIcon sx={{ fontSize: '14px !important' }} />}
                            label={t('common.original', 'Original')}
                            onClick={handleReset}
                            size="small"
                            sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                borderRadius: '12px',
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                '& .MuiChip-icon': { color: 'inherit' },
                                '&:hover': { bgcolor: 'primary.main' }
                            }}
                        />
                    </Tooltip>
                )}

                {translatedText && (
                    <Typography variant="caption" sx={{ opacity: 0.5, fontStyle: 'italic' }}>
                        AI Translated to {targetLanguage.name}
                    </Typography>
                )}
            </Box>

            {/* Language Picker Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        mt: 0.5,
                        minWidth: 220,
                        maxHeight: 400,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '800', mb: 1.5, color: 'primary.main' }}>
                        {t('common.selectLanguage', 'Select Language')}
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={t('common.searchLanguages', 'Search languages...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '10px', fontSize: '0.85rem' }
                        }}
                    />
                </Box>
                <Divider />
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {displayedLanguages.map((lang) => (
                        <MenuItem
                            key={lang.code}
                            onClick={() => selectLanguage(lang)}
                            selected={targetLanguage.code === lang.code}
                            sx={{ py: 1.2, px: 2 }}
                        >
                            <ListItemIcon sx={{ minWidth: '36px !important' }}>
                                {'isImage' in lang && lang.isImage ? (
                                    <Box
                                        component="img"
                                        src={lang.flag}
                                        sx={{ width: 22, height: 16, borderRadius: '2px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <span style={{ fontSize: 22 }}>{lang.flag}</span>
                                )}
                            </ListItemIcon>
                            <ListItemText primary={lang.name} primaryTypographyProps={{ variant: 'body2', fontWeight: targetLanguage.code === lang.code ? 600 : 400 }} />
                            {targetLanguage.code === lang.code && <CheckIcon fontSize="small" color="primary" />}
                        </MenuItem>
                    ))}

                    {!showMore && !searchQuery && (
                        <MenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowMore(true);
                            }}
                            sx={{ justifyContent: 'center', py: 1.5, color: 'primary.main' }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {t('common.moreLanguages', 'More Languages...')}
                            </Typography>
                        </MenuItem>
                    )}

                    {displayedLanguages.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('common.noResults', 'No results found')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Menu>
        </Box>
    );
};

export default TranslatingText;
